/* NarratorPanel — "Read-as-you-type" narrator + on-demand TTS for work.
 * Pure frontend: uses browser SpeechSynthesis API (free, instant, works on iOS Safari).
 * Live mode auto-reads each new word OR sentence as you type. */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Mic, X, Play, Pause, Square, RotateCcw, Settings, Volume2, Clipboard, Radio, Keyboard, Plus, Trash2, Sparkles, FileText, Wand2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  NARRATOR_ACTIONS, loadShortcuts, saveShortcuts, resetShortcuts,
  formatBinding, captureBinding,
} from '../utils/narratorShortcuts';
import {
  loadPhrases, savePhrases, newPhraseId,
} from '../utils/phraseSnippets';

const STORAGE_KEY = 'narrator_state_v1';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw);
  } catch { return {}; }
}

function saveState(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* ignore */ }
}

// Live-mode triggers
const SENTENCE_END = /[.!?\n]/;
const WORD_END = /[\s.,;:!?\n]/;

export default function NarratorPanel({ open, onClose }) {
  const stored = loadState();
  const [text, setText] = useState(stored.text || '');
  const [voices, setVoices] = useState([]);
  const [voiceURI, setVoiceURI] = useState(stored.voiceURI || '');
  const [rate, setRate] = useState(stored.rate || 1.0);
  const [pitch, setPitch] = useState(stored.pitch || 1.0);
  const [liveMode, setLiveMode] = useState(stored.liveMode || 'off'); // off | word | sentence
  const [playing, setPlaying] = useState(false);
  const [paused, setPaused] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const prevTextRef = useRef(stored.text || '');
  const speakingRef = useRef(false);
  const textareaRef = useRef(null);

  // Load voices (some browsers populate async)
  useEffect(() => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const load = () => {
      const list = window.speechSynthesis.getVoices();
      setVoices(list);
      if (!voiceURI && list.length > 0) {
        const sv = list.find((v) => v.lang.startsWith('sv'))
                || list.find((v) => v.lang.startsWith('en'))
                || list[0];
        if (sv) setVoiceURI(sv.voiceURI);
      }
    };
    load();
    window.speechSynthesis.onvoiceschanged = load;
    return () => { try { window.speechSynthesis.onvoiceschanged = null; } catch { /* */ } };
  }, []);

  useEffect(() => {
    saveState({ text, voiceURI, rate, pitch, liveMode });
  }, [text, voiceURI, rate, pitch, liveMode]);

  // Stop speech when modal closes
  useEffect(() => {
    if (!open) {
      try { window.speechSynthesis?.cancel(); } catch { /* */ }
      setPlaying(false); setPaused(false); speakingRef.current = false;
    }
  }, [open]);

  // Helper: speak some text (used by both modes)
  const queueSpeak = useCallback((chunk, { interrupt = false } = {}) => {
    if (typeof window === 'undefined' || !window.speechSynthesis) return;
    const t = (chunk || '').trim();
    if (!t) return;
    if (interrupt) {
      try { window.speechSynthesis.cancel(); } catch { /* */ }
    }
    const u = new SpeechSynthesisUtterance(t);
    const v = voices.find((vv) => vv.voiceURI === voiceURI);
    if (v) { u.voice = v; u.lang = v.lang; }
    u.rate = rate; u.pitch = pitch; u.volume = 1.0;
    u.onstart = () => { setPlaying(true); setPaused(false); speakingRef.current = true; };
    u.onpause = () => setPaused(true);
    u.onresume = () => setPaused(false);
    u.onend = () => {
      // only update playing if queue is empty
      setTimeout(() => {
        if (!window.speechSynthesis.speaking && !window.speechSynthesis.pending) {
          setPlaying(false); setPaused(false); speakingRef.current = false;
        }
      }, 50);
    };
    u.onerror = (e) => {
      if (e.error && !['interrupted', 'canceled'].includes(e.error)) {
        toast.error('Uppläsning misslyckades');
      }
    };
    window.speechSynthesis.speak(u);
  }, [voices, voiceURI, rate, pitch]);

  // Live mode handler — detect new word/sentence on each change
  const onTextChange = (e) => {
    const newText = e.target.value;
    const prev = prevTextRef.current;
    prevTextRef.current = newText;
    setText(newText);

    if (liveMode === 'off') return;
    if (newText.length <= prev.length) return; // deletion — don't speak
    // Detect what was just typed (usually 1 char, but could be paste)
    const added = newText.slice(prev.length);
    const lastChar = added[added.length - 1];

    if (liveMode === 'word' && WORD_END.test(lastChar)) {
      // Extract last word before the word-end char
      const trimmed = newText.slice(0, newText.length - 1);
      const match = trimmed.match(/(\S+)$/);
      if (match) queueSpeak(match[1]);
    } else if (liveMode === 'sentence' && SENTENCE_END.test(lastChar)) {
      // Extract last sentence — text between previous sentence-end and current one
      const upto = newText.slice(0, newText.length - 1);
      // find previous sentence-end
      let lastIdx = -1;
      for (let i = upto.length - 1; i >= 0; i--) {
        if (SENTENCE_END.test(upto[i])) { lastIdx = i; break; }
      }
      const sentence = upto.slice(lastIdx + 1).trim();
      if (sentence) queueSpeak(sentence);
    }
  };

  // Manual play of full text
  const speakAll = useCallback(() => {
    const t = text.trim();
    if (!t) { toast.error('Skriv något att läsa upp först 🌸'); return; }
    queueSpeak(t, { interrupt: true });
  }, [text, queueSpeak]);

  const playPause = () => {
    if (!window.speechSynthesis) {
      toast.error('Din webbläsare stödjer tyvärr inte uppläsning 😢');
      return;
    }
    if (!playing) {
      speakAll();
    } else if (paused) {
      window.speechSynthesis.resume();
    } else {
      window.speechSynthesis.pause();
    }
  };

  const stop = () => {
    try { window.speechSynthesis.cancel(); } catch { /* */ }
    setPlaying(false); setPaused(false); speakingRef.current = false;
  };

  const readSelection = () => {
    const sel = window.getSelection?.()?.toString();
    if (!sel || !sel.trim()) {
      toast.error('Markera text på sidan först — sen klicka här igen 🌸');
      return;
    }
    queueSpeak(sel, { interrupt: true });
  };

  const pasteFromClipboard = async () => {
    try {
      const clip = await navigator.clipboard.readText();
      if (clip) {
        prevTextRef.current = clip;
        setText(clip);
        toast.success('Inklistrat 📋');
      } else {
        toast.error('Urklippet är tomt');
      }
    } catch {
      toast.error('Kunde inte läsa urklipp');
    }
  };

  const clearText = () => {
    prevTextRef.current = '';
    setText('');
    stop();
  };

  if (!open) return null;

  // Group voices by language
  const voicesByLang = {};
  voices.forEach((v) => {
    const lang = v.lang || 'okänd';
    if (!voicesByLang[lang]) voicesByLang[lang] = [];
    voicesByLang[lang].push(v);
  });
  const sortedLangs = Object.keys(voicesByLang).sort((a, b) => {
    if (a.startsWith('sv')) return -1;
    if (b.startsWith('sv')) return 1;
    if (a.startsWith('en')) return -1;
    if (b.startsWith('en')) return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="fixed inset-0 z-[110] bg-black/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto"
         onClick={onClose} data-testid="narrator-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="neo-card bg-white w-full max-w-2xl p-4 space-y-3 max-h-[92vh] overflow-y-auto">
        <header className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FFC8DD] to-[#A2D2FF] flex items-center justify-center">
              <Mic className="w-4 h-4 text-white" />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase opacity-60 tracking-wide">Berättare</p>
              <h2 className="font-['Fredoka'] font-bold text-lg leading-tight">Läs upp medan jag skriver</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-black/5 rounded" data-testid="narrator-close">
            <X className="w-5 h-5" />
          </button>
        </header>

        {/* Live-mode picker — the star feature */}
        <div className="neo-card p-2 bg-gradient-to-br from-[#FFE8F5] to-[#E8F4FF]" data-testid="narrator-live-mode">
          <p className="text-[10px] font-bold uppercase opacity-60 tracking-wide flex items-center gap-1 mb-1">
            <Radio className="w-3 h-3" /> Live-läge
          </p>
          <div className="flex gap-1 flex-wrap">
            <button
              onClick={() => setLiveMode('off')}
              className={`neo-btn px-3 py-1.5 text-xs font-bold flex-1 ${liveMode === 'off' ? 'bg-white ring-2 ring-black/30' : 'bg-white/60 opacity-70'}`}
              data-testid="narrator-mode-off">
              💤 Av
            </button>
            <button
              onClick={() => setLiveMode('word')}
              className={`neo-btn px-3 py-1.5 text-xs font-bold flex-1 ${liveMode === 'word' ? 'bg-[#FFE8A3] ring-2 ring-[#D06892]' : 'bg-white/60 opacity-70'}`}
              data-testid="narrator-mode-word">
              💬 Ord-för-ord
            </button>
            <button
              onClick={() => setLiveMode('sentence')}
              className={`neo-btn px-3 py-1.5 text-xs font-bold flex-1 ${liveMode === 'sentence' ? 'bg-[#B9FBC0] ring-2 ring-[#D06892]' : 'bg-white/60 opacity-70'}`}
              data-testid="narrator-mode-sentence">
              📜 Mening-för-mening
            </button>
          </div>
          <p className="text-[10px] opacity-70 italic mt-1">
            {liveMode === 'off' && 'Live-läge av — klicka "Läs upp" för att höra all text.'}
            {liveMode === 'word' && 'Pet:en läser varje ord när du trycker mellanslag 🐾'}
            {liveMode === 'sentence' && 'Pet:en läser varje mening efter punkt, ! eller ? 🌸'}
          </p>
        </div>

        {/* Quick action buttons */}
        <div className="flex flex-wrap gap-1">
          <button onClick={pasteFromClipboard}
            className="neo-btn px-2 py-1 bg-[#FFE8F5] text-[11px] font-bold flex items-center gap-1"
            data-testid="narrator-paste">
            <Clipboard className="w-3 h-3" /> Klistra in
          </button>
          <button onClick={readSelection}
            className="neo-btn px-2 py-1 bg-[#FFE8A3] text-[11px] font-bold flex items-center gap-1"
            data-testid="narrator-read-selection"
            title="Markera text på sidan först, klicka sen här">
            👆 Läs markerad text
          </button>
          <button onClick={clearText}
            className="neo-btn px-2 py-1 bg-white text-[11px] font-bold flex items-center gap-1"
            data-testid="narrator-clear"
            disabled={!text}>
            <RotateCcw className="w-3 h-3" /> Rensa
          </button>
          <button onClick={() => setShowSettings(!showSettings)}
            className={`neo-btn px-2 py-1 text-[11px] font-bold flex items-center gap-1 ml-auto ${showSettings ? 'bg-[#A2D2FF]' : 'bg-white'}`}
            data-testid="narrator-settings-toggle">
            <Settings className="w-3 h-3" /> Inställningar
          </button>
        </div>

        {/* Settings panel */}
        {showSettings && (
          <div className="neo-card p-3 bg-[#FFFAF0] space-y-2" data-testid="narrator-settings">
            <div>
              <label className="text-[10px] font-bold uppercase opacity-60 block mb-1">Röst</label>
              {voices.length === 0 ? (
                <p className="text-xs opacity-60 italic">Laddar röster…</p>
              ) : (
                <select value={voiceURI} onChange={(e) => setVoiceURI(e.target.value)}
                  className="neo-input w-full text-xs"
                  data-testid="narrator-voice-select">
                  {sortedLangs.map((lang) => (
                    <optgroup key={lang} label={lang}>
                      {voicesByLang[lang].map((v) => (
                        <option key={v.voiceURI} value={v.voiceURI}>
                          {v.name} {v.localService ? '(lokal)' : '(online)'}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              )}
              <p className="text-[10px] opacity-50 italic mt-0.5">
                Svenska röster visas först.
              </p>
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase opacity-60 block mb-1">
                Hastighet: <b>{rate.toFixed(2)}×</b>
              </label>
              <input type="range" min="0.5" max="2.0" step="0.05"
                value={rate} onChange={(e) => setRate(parseFloat(e.target.value))}
                className="w-full" data-testid="narrator-rate" />
            </div>

            <div>
              <label className="text-[10px] font-bold uppercase opacity-60 block mb-1">
                Tonhöjd: <b>{pitch.toFixed(2)}</b>
              </label>
              <input type="range" min="0.5" max="2.0" step="0.05"
                value={pitch} onChange={(e) => setPitch(parseFloat(e.target.value))}
                className="w-full" data-testid="narrator-pitch" />
            </div>
          </div>
        )}

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={text}
          onChange={onTextChange}
          placeholder={liveMode === 'off'
            ? "Skriv eller klistra in text — klicka 'Läs upp' när du är klar.\n\nTips: Slå på 'Mening-för-mening' så läser pet:en varje mening direkt när du skriver punkt."
            : liveMode === 'word'
              ? "Skriv på — pet:en läser varje ord när du trycker mellanslag. Bra för att höra hur det låter när du formulerar dig 🐾"
              : "Skriv på — pet:en läser varje mening direkt när du skriver punkt, ! eller ?. Perfekt för att kolla flyt och stavning 🌸"}
          rows={10}
          className="neo-input w-full text-base leading-relaxed resize-y min-h-[200px]"
          data-testid="narrator-textarea"
          autoFocus
        />

        <div className="flex items-center justify-between gap-2">
          <p className="text-[10px] opacity-60">
            {text.length > 0 ? `${text.length} tecken · ~${Math.ceil(text.split(/\s+/).filter(Boolean).length / 150 / rate)} min uppläsning` : '\u00A0'}
          </p>
        </div>

        {/* Manual play / pause / stop */}
        <div className="flex gap-2">
          <button onClick={playPause} disabled={!text.trim()}
            className={`neo-btn flex-1 py-3 font-bold text-base flex items-center justify-center gap-2 ${playing && !paused ? 'bg-[#FFE8A3]' : 'bg-[#B9FBC0]'}`}
            data-testid="narrator-play">
            {playing && !paused
              ? <><Pause className="w-5 h-5" /> Pausa</>
              : playing && paused
                ? <><Play className="w-5 h-5" /> Fortsätt</>
                : <><Play className="w-5 h-5" /> Läs upp all text</>}
          </button>
          {playing && (
            <button onClick={stop}
              className="neo-btn px-4 py-3 bg-white font-bold text-sm flex items-center gap-1"
              data-testid="narrator-stop">
              <Square className="w-4 h-4" /> Stopp
            </button>
          )}
        </div>

        <p className="text-[10px] opacity-50 italic text-center pt-1 flex items-center justify-center gap-1">
          <Volume2 className="w-3 h-3" /> Webbläsarens inbyggda röst — gratis, snabb, fungerar offline 💖
        </p>

        <div className="neo-card p-3 bg-[#FFFAF0] space-y-2" data-testid="narrator-shortcuts-config">
          <ShortcutsConfig />
        </div>

        <div className="neo-card p-3 bg-[#F4F0FF] space-y-2" data-testid="narrator-phrases-section">
          <PhrasesSection />
        </div>
      </div>
    </div>
  );
}

// =================== SHORTCUTS CONFIG ===================
function ShortcutsConfig() {
  const [bindings, setBindings] = useState(() => loadShortcuts());
  const [recording, setRecording] = useState(null); // action id being recorded
  const [conflict, setConflict] = useState(null);

  useEffect(() => {
    if (!recording) return;
    const onKey = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === 'Escape') { setRecording(null); return; }
      const captured = captureBinding(e);
      if (!captured) return;
      // Detect conflict with another action
      const conflictWith = Object.entries(bindings).find(
        ([id, b]) => id !== recording &&
                     b.ctrl === captured.ctrl &&
                     b.alt === captured.alt &&
                     b.shift === captured.shift &&
                     (b.key || '').toLowerCase() === captured.key
      );
      if (conflictWith) {
        setConflict({ otherAction: conflictWith[0], combo: formatBinding(captured) });
        // Still allow if user confirms — for now block
        setRecording(null);
        return;
      }
      const next = { ...bindings, [recording]: captured };
      setBindings(next);
      saveShortcuts(next);
      setRecording(null);
      setConflict(null);
      toast.success(`✨ ${formatBinding(captured)} sparat`);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [recording, bindings]);

  const clear = (actionId) => {
    const next = { ...bindings };
    delete next[actionId];
    setBindings(next);
    saveShortcuts(next);
  };

  const reset = () => {
    if (!window.confirm('Återställ alla genvägar till standard?')) return;
    const fresh = resetShortcuts();
    setBindings(fresh);
    toast.success('Återställt 💛');
  };

  return (
    <div className="space-y-2" data-testid="narrator-shortcuts-list">
      <div className="flex items-center justify-between">
        <p className="font-bold flex items-center gap-1.5 text-xs">
          <Keyboard className="w-3.5 h-3.5" /> Mina kortkommandon
        </p>
        <button
          onClick={reset}
          data-testid="narrator-shortcuts-reset"
          className="text-[10px] text-rose-600 hover:text-rose-700 hover:underline"
        >
          Återställ
        </button>
      </div>
      {conflict && (
        <p className="text-[10px] text-rose-600 bg-rose-50 px-2 py-1 rounded">
          ⚠️ <strong>{conflict.combo}</strong> används redan av <strong>{NARRATOR_ACTIONS.find(a => a.id === conflict.otherAction)?.label}</strong>
        </p>
      )}
      <div className="space-y-1">
        {NARRATOR_ACTIONS.map((a) => {
          const b = bindings[a.id];
          const isRec = recording === a.id;
          return (
            <div
              key={a.id}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-white/70 hover:bg-white"
              data-testid={`narrator-shortcut-row-${a.id}`}
            >
              <span className="text-base">{a.emoji}</span>
              <span className="flex-1 text-[11px] text-gray-700">{a.label}</span>
              <button
                onClick={() => setRecording(isRec ? null : a.id)}
                data-testid={`narrator-shortcut-rebind-${a.id}`}
                className={`min-w-[110px] text-center px-2 py-1 rounded-md text-[10px] font-bold font-mono transition ${
                  isRec
                    ? 'bg-amber-100 text-amber-800 animate-pulse border-2 border-amber-400'
                    : 'bg-gray-100 text-gray-700 hover:bg-amber-50 border-2 border-transparent'
                }`}
                title={isRec ? 'Tryck genvägen nu (Esc avbryter)' : 'Klicka för att ändra'}
              >
                {isRec ? '⏺ Tryck nu…' : formatBinding(b)}
              </button>
              {b && b.key && (
                <button
                  onClick={() => clear(a.id)}
                  data-testid={`narrator-shortcut-clear-${a.id}`}
                  className="p-1 text-gray-400 hover:text-rose-500"
                  title="Ta bort"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[9px] text-gray-500 italic pt-1">
        💡 Klicka på rutan, tryck din kombo (t.ex. <code className="px-1 bg-white rounded">Ctrl+Alt+1</code>) — Esc avbryter
      </p>
    </div>
  );
}


// =================== PHRASES SECTION ===================
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PhrasesSection() {
  const [phrases, setPhrases] = useState(() => loadPhrases());
  const [adding, setAdding] = useState(false);
  const [newTrigger, setNewTrigger] = useState(';');
  const [newExpansion, setNewExpansion] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiBusy, setAiBusy] = useState(false);

  useEffect(() => {
    const reload = () => setPhrases(loadPhrases());
    window.addEventListener('phrases-changed', reload);
    return () => window.removeEventListener('phrases-changed', reload);
  }, []);

  const persist = (next) => {
    setPhrases(next);
    savePhrases(next);
  };

  const saveNew = () => {
    if (!newTrigger.trim() || !newExpansion.trim()) {
      toast.info('Skriv både trigger och text 💛');
      return;
    }
    const p = {
      id: newPhraseId(),
      trigger: newTrigger.trim().slice(0, 24),
      expansion: newExpansion.trim().slice(0, 600),
      enabled: true,
      used_count: 0,
    };
    persist([p, ...phrases]);
    setAdding(false);
    setNewTrigger(';');
    setNewExpansion('');
    toast.success(`✨ ${p.trigger} sparat`);
  };

  const updatePhrase = (id, patch) => {
    persist(phrases.map((p) => p.id === id ? { ...p, ...patch } : p));
  };

  const removePhrase = (id) => {
    persist(phrases.filter((p) => p.id !== id));
  };

  const aiSuggest = async () => {
    if (!aiPrompt.trim()) { toast.info('Skriv ett ämne, t.ex. "kundmejl" 💛'); return; }
    setAiBusy(true);
    try {
      const r = await axios.post(`${API}/phrases/suggest`, {
        topic: aiPrompt, count: 5,
      }, { withCredentials: true });
      const suggestions = r.data?.suggestions || [];
      if (!suggestions.length) throw new Error('Tomma förslag');
      const newPhrases = suggestions.slice(0, 6).map((s) => ({
        id: newPhraseId(),
        trigger: (s.trigger || ';nytt').trim().slice(0, 24),
        expansion: (s.expansion || '').trim().slice(0, 600),
        enabled: true,
        used_count: 0,
      })).filter((p) => p.trigger && p.expansion);
      persist([...newPhrases, ...phrases]);
      setAiPrompt('');
      toast.success(`✨ ${newPhrases.length} nya snippets tillagda`);
    } catch (e) {
      toast.error('Kunde inte föreslå just nu 💔');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="narrator-phrases-list">
      <div className="flex items-center justify-between">
        <p className="font-bold flex items-center gap-1.5 text-xs">
          <FileText className="w-3.5 h-3.5" /> Mina fraser
          <span className="text-[10px] font-normal opacity-60">({phrases.length})</span>
        </p>
        <button
          onClick={() => setAdding(!adding)}
          data-testid="narrator-phrase-add-btn"
          className="text-[10px] px-2 py-0.5 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-bold flex items-center gap-1"
        >
          <Plus className="w-3 h-3" /> {adding ? 'Stäng' : 'Ny'}
        </button>
      </div>

      <p className="text-[10px] text-gray-500 italic leading-tight">
        Skriv din trigger (t.ex. <code className="px-1 bg-white rounded">;hej</code>) + mellanslag i vilket textfält som helst — hela meningen kommer in automatiskt ✨
      </p>

      {adding && (
        <div className="bg-white rounded-lg p-2 space-y-1.5 border border-violet-200" data-testid="narrator-phrase-add-form">
          <div className="flex gap-1.5">
            <input
              value={newTrigger}
              onChange={(e) => setNewTrigger(e.target.value)}
              placeholder=";xx"
              data-testid="narrator-phrase-new-trigger"
              className="w-24 px-2 py-1 rounded border border-violet-200 focus:border-violet-500 outline-none text-xs font-mono"
              maxLength={24}
            />
            <textarea
              value={newExpansion}
              onChange={(e) => setNewExpansion(e.target.value)}
              placeholder="Texten som ska skrivas ut..."
              data-testid="narrator-phrase-new-expansion"
              rows={2}
              className="flex-1 px-2 py-1 rounded border border-violet-200 focus:border-violet-500 outline-none text-xs resize-y"
              maxLength={600}
            />
          </div>
          <button
            onClick={saveNew}
            data-testid="narrator-phrase-save"
            className="w-full text-[10px] py-1 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-bold"
          >
            Spara
          </button>
        </div>
      )}

      <div className="flex gap-1.5">
        <input
          value={aiPrompt}
          onChange={(e) => setAiPrompt(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && aiSuggest()}
          placeholder='✨ AI: säg ett ämne'
          data-testid="narrator-phrase-ai-input"
          className="flex-1 px-2 py-1 rounded border border-violet-200 bg-white/70 focus:border-violet-500 outline-none text-xs"
        />
        <button
          onClick={aiSuggest}
          disabled={aiBusy}
          data-testid="narrator-phrase-ai-btn"
          className="px-2 py-1 rounded-md bg-gradient-to-r from-pink-400 to-violet-500 text-white text-[10px] font-bold flex items-center gap-1 disabled:opacity-50"
        >
          <Wand2 className="w-3 h-3" /> {aiBusy ? '…' : 'AI'}
        </button>
      </div>

      <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
        {phrases.length === 0 && (
          <p className="text-[10px] text-gray-400 italic text-center py-2">
            Inga fraser än — klicka "Ny" eller låt AI:n föreslå 💛
          </p>
        )}
        {phrases.map((p) => {
          const isEditing = editingId === p.id;
          return (
            <div
              key={p.id}
              data-testid={`narrator-phrase-${p.id}`}
              className={`rounded-lg transition ${
                isEditing ? 'bg-white border-2 border-violet-300' : 'bg-white/70 hover:bg-white border border-violet-100'
              }`}
            >
              {!isEditing ? (
                <div className="flex items-center gap-1.5 px-2 py-1">
                  <button
                    onClick={() => updatePhrase(p.id, { enabled: !p.enabled })}
                    data-testid={`narrator-phrase-toggle-${p.id}`}
                    className={`w-6 h-3.5 rounded-full relative transition flex-shrink-0 ${p.enabled ? 'bg-emerald-400' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 w-2.5 h-2.5 bg-white rounded-full shadow transition-transform ${p.enabled ? 'translate-x-3' : 'translate-x-0.5'}`} />
                  </button>
                  <code className="px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-mono font-bold flex-shrink-0">
                    {p.trigger}
                  </code>
                  <span className="text-violet-300 text-[10px]">→</span>
                  <span className="flex-1 text-[11px] text-gray-700 truncate" title={p.expansion}>{p.expansion}</span>
                  <button
                    onClick={() => setEditingId(p.id)}
                    data-testid={`narrator-phrase-edit-${p.id}`}
                    className="p-0.5 text-violet-500 hover:bg-violet-100 rounded"
                  >
                    <Settings className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => removePhrase(p.id)}
                    data-testid={`narrator-phrase-del-${p.id}`}
                    className="p-0.5 text-rose-400 hover:bg-rose-100 rounded"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <div className="p-2 space-y-1.5">
                  <div className="flex gap-1.5">
                    <input
                      value={p.trigger}
                      onChange={(e) => updatePhrase(p.id, { trigger: e.target.value })}
                      data-testid={`narrator-phrase-trigger-${p.id}`}
                      className="w-24 px-1.5 py-1 rounded border border-violet-200 focus:border-violet-500 outline-none text-xs font-mono"
                      maxLength={24}
                    />
                    <textarea
                      value={p.expansion}
                      onChange={(e) => updatePhrase(p.id, { expansion: e.target.value })}
                      data-testid={`narrator-phrase-expansion-${p.id}`}
                      rows={2}
                      className="flex-1 px-1.5 py-1 rounded border border-violet-200 focus:border-violet-500 outline-none text-xs resize-y"
                      maxLength={600}
                    />
                  </div>
                  <button
                    onClick={() => setEditingId(null)}
                    data-testid={`narrator-phrase-done-${p.id}`}
                    className="w-full text-[10px] py-1 rounded-md bg-violet-500 hover:bg-violet-600 text-white font-bold"
                  >
                    Klar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
